import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { loadSheet, saveSheet } from "./db/indexedDB";
import RibbonBar from "./components/RibbonBar/RibbonBar";
import FormulaBar from "./components/FormulaBar/FormulaBar";
import VirtualizedGrid from "./components/VirtualizedGrid/VirtualizedGrid";
import LuckysheetWrapper from "./components/LuckysheetWrapper";
import ShortcutsHelp from "./components/ShortcutsHelp/ShortcutsHelp";
import { connectWebSocket, broadcastChange } from "./network/wsClient";
import { shortcutManager, createDefaultShortcuts } from "./utils/shortcuts";

const INITIAL_ROWS = 1000;
const INITIAL_COLS = 26;

interface CellData {
  value: string;
  format?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    fontSize?: string;
    fontFamily?: string;
    fontColor?: string;
    backgroundColor?: string;
    alignment?: string;
    borderStyle?: string;
    numberFormat?: string;
  };
}

function getEmptySheet(rows = INITIAL_ROWS, cols = INITIAL_COLS): CellData[][] {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ value: "" }))
  );
}

function App() {
  const [data, setData] = useState<CellData[][]>(getEmptySheet());
  const [filters, setFilters] = useState<{ [col: number]: any }>({});
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
  const [selectedRange, setSelectedRange] = useState<{ start: { row: number; col: number }; end: { row: number; col: number } } | null>(null);
  const [formula, setFormula] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [sheetName, setSheetName] = useState("Sheet1");
  const [undoStack, setUndoStack] = useState<CellData[][][]>([]);
  const [redoStack, setRedoStack] = useState<CellData[][][]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [zoom, setZoom] = useState(100);
  const gridRef = useRef<HTMLDivElement>(null);
  const hiddenTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Clipboard sync state
  const [clipboardData, setClipboardData] = useState<string>("");
  const [pendingPaste, setPendingPaste] = useState(false);
  
  // Toggle between custom grid and Luckysheet
  const [useLuckysheet, setUseLuckysheet] = useState(false);

  useEffect(() => {
    loadSheet().then((d) => {
      if (d) setData(d);
      connectWebSocket((remoteData) => setData(remoteData), d || getEmptySheet());
      setIsLoading(false);
    }).catch((err) => {
      setError("Failed to load spreadsheet");
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.focus();
    }
  }, [isLoading]);

  const saveToHistory = useCallback((newData: CellData[][]) => {
    setUndoStack(prev => [...prev, data]);
    setRedoStack([]); // Clear redo stack when new action is performed
  }, [data]);

  const handleCellChange = useCallback((row: number, col: number, value: string) => {
    saveToHistory(data);
    const newData = data.map((r, i) =>
      i === row ? r.map((c, j) => (j === col ? { ...c, value } : c)) : r
    );
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
  }, [data, saveToHistory]);

  const handleFormatChange = useCallback((formatType: string, value: any) => {
    const { row, col } = selectedCell;
    saveToHistory(data);
    const newData = [...data];
    if (!newData[row]) return;
    
    if (!newData[row][col]) {
      newData[row][col] = { value: "" };
    }
    
    if (!newData[row][col].format) {
      newData[row][col].format = {};
    }
    
    newData[row][col].format = {
      ...newData[row][col].format,
      [formatType]: value
    };
    
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
  }, [selectedCell, data, saveToHistory]);

  const handleInsertRow = useCallback(() => {
    const { row } = selectedCell;
    saveToHistory(data);
    const newRow: CellData[] = Array.from({ length: INITIAL_COLS }, () => ({ value: "" }));
    const newData = [...data];
    newData.splice(row + 1, 0, newRow);
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
  }, [selectedCell, data, saveToHistory]);

  const handleInsertColumn = useCallback(() => {
    const { col } = selectedCell;
    saveToHistory(data);
    const newData = data.map(row => {
      const newRow = [...row];
      newRow.splice(col + 1, 0, { value: "" });
      return newRow;
    });
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
  }, [selectedCell, data, saveToHistory]);

  const handleDeleteRow = useCallback(() => {
    const { row } = selectedCell;
    if (data.length <= 1) return; // Keep at least one row
    saveToHistory(data);
    const newData = data.filter((_, i) => i !== row);
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
    // Adjust selection if needed
    if (row >= newData.length) {
      setSelectedCell({ row: newData.length - 1, col: selectedCell.col });
    }
  }, [selectedCell, data, saveToHistory]);

  const handleDeleteColumn = useCallback(() => {
    const { col } = selectedCell;
    if (data[0]?.length <= 1) return; // Keep at least one column
    saveToHistory(data);
    const newData = data.map(row => row.filter((_, i) => i !== col));
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
    // Adjust selection if needed
    if (col >= newData[0]?.length) {
      setSelectedCell({ row: selectedCell.row, col: newData[0]?.length - 1 || 0 });
    }
  }, [selectedCell, data, saveToHistory]);

  const handleFormula = useCallback((value: string) => {
    setFormula(value);
    // TODO: Implement formula calculation
    // For now, just update the cell value
    if (value.startsWith('=')) {
      // Simple formula handling
      const formulaValue = value.substring(1);
      handleCellChange(selectedCell.row, selectedCell.col, formulaValue);
    }
    // Refocus grid after editing formula
    setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.focus();
      }
    }, 0);
  }, [selectedCell, handleCellChange]);

  const handleUndo = useCallback(() => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack(prev => [...prev, data]);
      setUndoStack(prev => prev.slice(0, -1));
      setData(previousState);
      saveSheet(previousState);
      broadcastChange(previousState);
    }
  }, [undoStack, data]);

  const handleRedo = useCallback(() => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      setUndoStack(prev => [...prev, data]);
      setRedoStack(prev => prev.slice(0, -1));
      setData(nextState);
      saveSheet(nextState);
      broadcastChange(nextState);
    }
  }, [redoStack, data]);

  const handleNewSheet = useCallback(() => {
    saveToHistory(data);
    const newData = getEmptySheet();
    setData(newData);
    saveSheet(newData);
    broadcastChange(newData);
    setSelectedCell({ row: 0, col: 0 });
    setFormula("");
    setSelectedRange(null);
  }, [data, saveToHistory]);

  const handleSave = useCallback(() => {
    saveSheet(data);
    // Show a brief success message
    const statusBar = document.querySelector('.status-message');
    if (statusBar) {
      statusBar.textContent = 'Saved successfully';
      setTimeout(() => {
        statusBar.textContent = 'Ready';
      }, 2000);
    }
  }, [data]);

  // Additional shortcut handlers
  const handleOpenFile = useCallback(() => {
    // TODO: Implement file opening
    console.log('Open file functionality');
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedRange({
      start: { row: 0, col: 0 },
      end: { row: data.length - 1, col: data[0]?.length - 1 || 0 }
    });
  }, [data]);

  const handleFind = useCallback(() => {
    // TODO: Implement find functionality
    console.log('Find functionality');
  }, []);

  const handleReplace = useCallback(() => {
    // TODO: Implement replace functionality
    console.log('Replace functionality');
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleHelp = useCallback(() => {
    setShowShortcutsHelp(true);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 10, 200));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 10, 50));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(100);
  }, []);

  const handleCopy = useCallback(() => {}, []);
  const handlePaste = useCallback(() => {}, []);
  const handleCut = useCallback(() => {}, []);

  // Setup shortcuts
  useEffect(() => {
    const shortcuts = createDefaultShortcuts({
      undo: handleUndo,
      redo: handleRedo,
      save: handleSave,
      newSheet: handleNewSheet,
      openFile: handleOpenFile,
      copy: handleCopy,
      paste: handlePaste,
      cut: handleCut,
      selectAll: handleSelectAll,
      bold: () => handleFormatChange("bold", !data[selectedCell.row]?.[selectedCell.col]?.format?.bold),
      italic: () => handleFormatChange("italic", !data[selectedCell.row]?.[selectedCell.col]?.format?.italic),
      underline: () => handleFormatChange("underline", !data[selectedCell.row]?.[selectedCell.col]?.format?.underline),
      insertRow: handleInsertRow,
      insertColumn: handleInsertColumn,
      deleteRow: handleDeleteRow,
      deleteColumn: handleDeleteColumn,
      find: handleFind,
      replace: handleReplace,
      print: handlePrint,
      help: handleHelp,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      zoomReset: handleZoomReset
    });

    shortcutManager.addShortcuts(shortcuts);

    return () => {
      // Cleanup shortcuts when component unmounts
      shortcuts.forEach(shortcut => {
        shortcutManager.removeShortcut(shortcut.key, shortcut.ctrl, shortcut.shift, shortcut.alt, shortcut.meta);
      });
    };
  }, [
    handleUndo, handleRedo, handleSave, handleNewSheet, handleOpenFile,
    handleSelectAll, handleFormatChange,
    handleInsertRow, handleInsertColumn, handleDeleteRow, handleDeleteColumn,
    handleFind, handleReplace, handlePrint, handleHelp,
    handleZoomIn, handleZoomOut, handleZoomReset,
    data, selectedCell
  ]);

  // Memoize expensive calculations
  const gridInfo = useMemo(() => ({
    rows: data.length,
    columns: data[0]?.length || 0
  }), [data]);

  // Global keydown handler for shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only handle if not editing formula bar or cell
      const isFormulaBar = (e.target as HTMLElement)?.classList?.contains('formula-bar-input');
      if (isFormulaBar) return;
      // Ctrl+C/X/V: focus hidden textarea to let browser handle clipboard
      if (e.ctrlKey && ['c', 'x', 'v'].includes(e.key.toLowerCase())) {
        if (hiddenTextareaRef.current) {
          hiddenTextareaRef.current.value = '';
          hiddenTextareaRef.current.focus();
          if (e.key.toLowerCase() === 'v') {
            setPendingPaste(true);
          }
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Handle paste event from hidden textarea
  useEffect(() => {
    if (!pendingPaste) return;
    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain') || '';
      setClipboardData(text);
      setPendingPaste(false);
      // Refocus grid after paste
      setTimeout(() => {
        if (gridRef.current) gridRef.current.focus();
      }, 0);
    };
    const textarea = hiddenTextareaRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handlePaste);
      textarea.focus();
    }
    return () => {
      if (textarea) textarea.removeEventListener('paste', handlePaste);
    };
  }, [pendingPaste]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading spreadsheet...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Spreadsheet</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" style={{ zoom: `${zoom}%` }}>
      {/* Hidden textarea for clipboard events */}
      <textarea ref={hiddenTextareaRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0 }} tabIndex={-1} aria-hidden="true" />
      {/* Header with sheet name and basic controls */}
      <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <input
            type="text"
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            className="text-lg font-semibold border-none outline-none bg-transparent hover:bg-gray-100 px-2 py-1 rounded"
          />
          <div className="flex items-center space-x-2">
            <button
              onClick={handleNewSheet}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              title="New Sheet (Ctrl+N)"
            >
              New Sheet
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              title="Save (Ctrl+S)"
            >
              Save
            </button>
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              Undo
            </button>
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              Redo
            </button>
            <button
              onClick={handleHelp}
              className="px-3 py-1 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
              title="Help (F1)"
            >
              Help
            </button>
            <button
              onClick={() => setUseLuckysheet(!useLuckysheet)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                useLuckysheet 
                  ? 'bg-orange-500 text-white hover:bg-orange-600' 
                  : 'bg-indigo-500 text-white hover:bg-indigo-600'
              }`}
              title="Toggle between Custom Grid and Luckysheet"
            >
              {useLuckysheet ? 'Switch to Custom Grid' : 'Switch to Luckysheet'}
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleZoomOut}
              className="px-2 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              title="Zoom Out (Ctrl+-)"
            >
              -
            </button>
            <span className="text-sm text-gray-600 min-w-[60px] text-center">{zoom}%</span>
            <button
              onClick={handleZoomIn}
              className="px-2 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              title="Zoom In (Ctrl+=)"
            >
              +
            </button>
          </div>
          <div className="text-sm text-gray-500">
            {gridInfo.rows} rows × {gridInfo.columns} columns
          </div>
        </div>
      </div>

      {!useLuckysheet && (
        <>
          <RibbonBar 
            onFormatChange={handleFormatChange}
            onInsertRow={handleInsertRow}
            onInsertColumn={handleInsertColumn}
            onDeleteRow={handleDeleteRow}
            onDeleteColumn={handleDeleteColumn}
            selectedCell={selectedCell}
            selectedRange={selectedRange}
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={undoStack.length > 0}
            canRedo={redoStack.length > 0}
          />
          
          <FormulaBar
            value={formula}
            onChange={handleFormula}
            selectedCell={selectedCell}
            selectedRange={selectedRange}
          />
        </>
      )}
      
      <div className="flex-1 overflow-hidden bg-white">
        {useLuckysheet ? (
          <LuckysheetWrapper 
            onDataChange={(newData) => {
              console.log('Luckysheet data changed:', newData);
              // You can implement data conversion here if needed
            }}
          />
        ) : (
          <VirtualizedGrid
            ref={gridRef}
            data={data}
            onCellChange={handleCellChange}
            onFormatChange={handleFormatChange}
            filters={filters}
            selectedCell={selectedCell}
            setSelectedCell={setSelectedCell}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            setFilters={setFilters}
            onInsertRow={handleInsertRow}
            onInsertColumn={handleInsertColumn}
            onDeleteRow={handleDeleteRow}
            onDeleteColumn={handleDeleteColumn}
            clipboardData={clipboardData}
            setClipboardData={setClipboardData}
          />
        )}
      </div>

      {/* Status Bar */}
      <div className="bg-white border-t border-gray-300 px-4 py-1 flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          <span className="status-message">Ready</span>
          <span>•</span>
          <span>Cell: {String.fromCharCode(65 + selectedCell.col)}{selectedCell.row + 1}</span>
          {selectedRange && (
            <>
              <span>•</span>
              <span>Range: {String.fromCharCode(65 + selectedRange.start.col)}{selectedRange.start.row + 1}:{String.fromCharCode(65 + selectedRange.end.col)}{selectedRange.end.row + 1}</span>
            </>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <span>{zoom}%</span>
          <span>•</span>
          <span>Normal</span>
          <span>•</span>
          <span>Undo: {undoStack.length} | Redo: {redoStack.length}</span>
        </div>
      </div>

      {/* Shortcuts Help Modal */}
      <ShortcutsHelp 
        isOpen={showShortcutsHelp} 
        onClose={() => setShowShortcutsHelp(false)} 
      />
    </div>
  );
}

export default App; 